import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { InvoiceReversibleResponse } from '@aiwyn/gql-api/src/models/response/invoice-reversible-response.model';
import { ReverseInvoiceResponse } from '@aiwyn/gql-api/src/models/response/reverse-invoice-response.model';
import { InvoiceApiService } from '@aiwyn/gql-api/src/services/secure/v1/invoice-api.service';
import { DialogComponent, DialogSize } from '@anduin/shared';
import { DialogHeaderComponent } from '@anduin/shared';
import { DialogFooterComponent } from '@anduin/shared';
import { ButtonDirective } from '@anduin/shared';
import { IconComponent } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { InputErrorMessageDirective } from 'src/app/shared/directives/input-error-message/input-error-message.directive';
import { ConflictingDraftInvoice } from '../../../../../api/models/invoice-reversal-eligibility.models';
import { LaunchModalInvoice } from '../../../../../features/billing/state/billing-container/billing-container.actions';
import { InvoiceReversalConflictMapper } from './invoice-reversal-conflict.mapper';
import { InvoiceReversalDialogData } from './invoice-reversal-dialog.models';

@Component({
  selector: 'app-invoice-reversal-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    DialogComponent,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonDirective,
    IconComponent,
    InputErrorMessageDirective,
  ],
  templateUrl: './invoice-reversal-dialog.component.html',
  styleUrl: './invoice-reversal-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceReversalDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<InvoiceReversalDialogComponent>);
  private readonly invoiceApi = inject(InvoiceApiService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  readonly data = inject<InvoiceReversalDialogData>(MAT_DIALOG_DATA);
  readonly dialogSize = DialogSize.SMALL;

  readonly isLoading = signal(true);
  readonly isSubmitting = signal(false);
  readonly canReverse = signal(true);

  readonly conflictingDraftInvoices = signal<ConflictingDraftInvoice[]>([]);

  readonly MAX_REASON_LENGTH = 512;

  readonly reversalForm = this.fb.group({
    reason: ['', [Validators.maxLength(this.MAX_REASON_LENGTH)]],
  });

  private terminalEmitted = false;

  constructor() {
    this.checkReversibility();
    // Any close without onSubmit/onSuccess/onFailure/onRejected is treated as user-cancel
    // so analytics can distinguish abandoned reversals from completed attempts.
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.terminalEmitted) {
          this.data.tracking?.onCancel?.();
        }
      });
  }

  private async checkReversibility(): Promise<void> {
    try {
      const response: InvoiceReversibleResponse = await this.invoiceApi.invoiceReversible({
        id: this.data.invoiceId,
      });

      this.canReverse.set(response.reversible);

      if (!response.reversible && response.conflicts) {
        const conflicts = InvoiceReversalConflictMapper.toConflictingDraftInvoices(response.conflicts);
        this.conflictingDraftInvoices.set(conflicts);
      }
    } catch {
      this.canReverse.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  openDraftInvoice(draftInvoiceId: number): void {
    this.dialogRef.close(false);
    this.router.navigate(['billing', 'loadDraftBill'], { queryParams: { draftId: draftInvoiceId }, queryParamsHandling: 'merge' });
  }

  openProject(projectId: number): void {
    this.dialogRef.close(false);
    this.router.navigate(['/projects/job', projectId, 'firm-tasks']);
  }

  async confirmReversal(): Promise<void> {
    this.isSubmitting.set(true);
    this.terminalEmitted = true;
    this.data.tracking?.onSubmit?.();
    try {
      const response: ReverseInvoiceResponse = await this.invoiceApi.reverseInvoice({
        id: this.data.invoiceId,
        reversalReason: this.reversalForm.value.reason?.trim() || '',
      });

      const successResponse = response.draftInvoiceId !== 0;

      if (successResponse) {
        this.data.tracking?.onSuccess?.({ reversed: true, draftInvoiceId: response.draftInvoiceId });
      } else {
        this.data.tracking?.onRejected?.();
      }

      this.dialogRef.close(successResponse);
    } catch (err) {
      this.data.tracking?.onFailure?.(err);
      this.dialogRef.close(false);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
